import type { EditablePage } from "../../types";

import type { GeneratePreviewRequest, GeneratePreviewResponse } from "./worker";

let worker: Worker | null = null;

function getWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL("./worker.ts", import.meta.url), {
    type: "module",
  });
  return worker;
}

const pending = new Map<
  string,
  { resolve: (url: string) => void; reject: (err: any) => void }
>();

if (typeof window !== "undefined") {
  getWorker().onmessage = (ev: MessageEvent<GeneratePreviewResponse>) => {
    const { id } = ev.data;
    const entry = pending.get(id);
    if (!entry) return;

    if (ev.data.ok) {
      const blob = new Blob([ev.data.bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      entry.resolve(url);
    } else {
      entry.reject(new Error(ev.data.error));
    }
    pending.delete(id);
  };
}

const requestCache = new Map<string, Promise<string>>();

export async function generatePreviewPdf(
  pages: EditablePage[]
): Promise<string> {
  const requestKey = pages
    .map((p) => `${p.sourcePdfUrl}:${p.sourcePageIndex}`)
    .join("|");

  if (requestCache.has(requestKey)) {
    return requestCache.get(requestKey)!;
  }

  const id = crypto.randomUUID();
  const payload: GeneratePreviewRequest = {
    id,
    pages: pages.map((p) => ({
      sourcePdfUrl: p.sourcePdfUrl,
      sourcePageIndex: p.sourcePageIndex,
    })),
  };

  const promise = new Promise<string>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    getWorker().postMessage(payload);
  });

  requestCache.set(requestKey, promise);

  // Clean up cache after request completes
  promise.finally(() => {
    setTimeout(() => requestCache.delete(requestKey), 1000);
  });

  return promise;
}
